/**
 * EmployerMembersPage.jsx — Phase 0.7 S7/S8 member-management workspace.
 *
 * Role-aware single-pane workspace consuming the FastAPI shim for
 * every lifecycle write and the live S6 RLS for every read.
 *
 * Routes mounted at /employer/members.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { apiFetch } from '@/lib/api';
import {
  fetchMyMembership,
  listMembers,
  listInvitations,
  listSuspensions,
  listAuditFeed,
} from '@/lib/employerMembersData';
import {
  inviteMember,
  revokeInvitation,
  revokeMember,
  modifyRole,
  suspendMember,
  unsuspend,
  bulkRevokeMembers,
  bulkRoleModify,
  bulkRevokeInvitations,
  bulkUnsuspend,
  BULK_LIMITS,
} from '@/lib/employerMembers';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';

import { MembersList } from '@/components/employer/members/MembersList';
import { InvitationsList } from '@/components/employer/members/InvitationsList';
import { SuspensionsList } from '@/components/employer/members/SuspensionsList';
import { AuditFeed } from '@/components/employer/members/AuditFeed';
import { InviteDialog } from '@/components/employer/members/InviteDialog';
import { RoleModifyDialog } from '@/components/employer/members/RoleModifyDialog';
import { SuspendDialog } from '@/components/employer/members/SuspendDialog';
import { BulkResultModal } from '@/components/employer/members/BulkResultModal';
import { ReviewerView } from '@/components/employer/members/ReviewerView';
import { EmployerNoMembershipStub } from '@/components/employer/EmployerNoMembershipStub';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { ROLE_LABEL, ROLE_DESCRIPTION } from '@/lib/employerAuthority';


export default function EmployerMembersPage() {
  const navigate = useNavigate();

  const [accountId,   setAccountId]   = useState(null);
  const [orgName,     setOrgName]     = useState('');
  const [myRole,      setMyRole]      = useState(null);
  const [myUserId,    setMyUserId]    = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [accessError, setAccessError] = useState(null);
  // F-D4 (RC 1.62) — distinguish "no membership / no org yet"
  // from a recoverable network error. Only the former renders
  // the calm empty-workspace stub.
  const [noMembership, setNoMembership] = useState(false);

  const [members,     setMembers]     = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [suspensions, setSuspensions] = useState([]);
  const [audit,       setAudit]       = useState([]);

  const [selectedMemberIds,     setSelectedMemberIds]     = useState(new Set());
  const [selectedInvitationIds, setSelectedInvitationIds] = useState(new Set());
  const [selectedSuspensionIds, setSelectedSuspensionIds] = useState(new Set());

  const [inviteOpen,   setInviteOpen]   = useState(false);
  const [roleDialog,   setRoleDialog]   = useState(null);   // { member }
  const [suspendDialog,setSuspendDialog]= useState(null);   // { member }
  const [bulkResult,   setBulkResult]   = useState(null);   // { title, summary }

  /* ─── boot: derive account_id + role from /api/employer/v1/me ─── */
  const bootstrap = useCallback(async () => {
    setLoading(true);
    setAccessError(null);
    try {
      const me = await apiFetch('/api/employer/v1/me');
      const orgId = me?.organisation?.id;
      const role  = me?.member?.role;
      if (!orgId || !role) {
        // F-D4 (RC 1.62 employer-auth doctrine) — the user is
        // authenticated and has employer_access=true, but they
        // are not yet attached to an organisation. Render the
        // empty-workspace stub rather than a harsh access-denied
        // card. `noMembership` is a distinct mode from
        // `accessError` (which is for unrecoverable failures).
        setNoMembership(true);
        setLoading(false);
        return;
      }
      setAccountId(orgId);
      setOrgName(me.organisation.name || 'Organisation');
      setMyRole(role);
      setMyUserId(me.user_id);
    } catch (e) {
      // 404 = no organisation yet → same calm stub as the
      // missing-member-row case above.
      if (e?.status === 404) {
        setNoMembership(true);
      } else {
        setAccessError(String(e?.message || e || 'failed to load workspace'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  /* ─── data refresh ─── */
  const refresh = useCallback(async () => {
    if (!accountId) return;
    const [m, i, s, a] = await Promise.all([
      listMembers(accountId).catch(() => []),
      listInvitations(accountId).catch(() => []),
      listSuspensions(accountId).catch(() => []),
      listAuditFeed(accountId, { limit: 100 }).catch(() => []),
    ]);
    setMembers(m);
    setInvitations(i);
    setSuspensions(s);
    setAudit(a);
  }, [accountId]);

  useEffect(() => { if (accountId) refresh(); }, [accountId, refresh]);

  /* ─── action handlers (single row) ─── */
  const onInvite = useCallback(async ({ targetEmail, targetRole }) => {
    try {
      await inviteMember({ accountId, targetEmail, targetRole });
      toast.success('invitation sent');
      setInviteOpen(false);
      await refresh();
    } catch (e) { toast.error(String(e?.detail || e?.message || 'invite failed')); }
  }, [accountId, refresh]);

  const onRevokeInvitation = useCallback(async (id) => {
    try {
      await revokeInvitation({ accountId, invitationId: id });
      toast.success('invitation revoked');
      await refresh();
    } catch (e) { toast.error(String(e?.detail || e?.message || 'revoke failed')); }
  }, [accountId, refresh]);

  /* Pass-1 Beta Activation — Slice 5 reissue handler.
   *
   * "Reissue" is NOT a backend resend (no `_resend` RPC exists).
   * It is a frontend-composed two-step:
   *   1. POST /invitations/{id}/revoke    (existing endpoint)
   *   2. POST /members/invite             (existing endpoint, mints
   *      a fresh row + fresh token + fresh expiry)
   * Both calls carry per-call Idempotency-Key headers. Partial
   * failures are surfaced honestly to the operator — we do NOT
   * pretend the action is atomic.
   *
   * A reissue ALWAYS generates a NEW invitation link. The previous
   * link becomes invalid the moment step 1 succeeds.
   */
  const onReissueInvitation = useCallback(async (inv) => {
    if (!inv?.id || !inv?.target_email || !inv?.target_role) {
      toast.error('Invitation missing fields — refresh and try again.');
      return;
    }
    const mintKey = (label) =>
      `reissue-${label}-${inv.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    // Step 1 — revoke the existing pending invitation.
    try {
      await revokeInvitation({
        accountId,
        invitationId: inv.id,
        idempotencyKey: mintKey('revoke'),
      });
    } catch (e) {
      toast.error(
        `Reissue blocked at step 1 — couldn't revoke the existing invitation. ${
          e?.detail || e?.message || ''
        }`.trim(),
      );
      return;
    }

    // Step 2 — issue a fresh invitation with the same email + role.
    try {
      await inviteMember({
        accountId,
        targetEmail: inv.target_email,
        targetRole:  inv.target_role,
        idempotencyKey: mintKey('issue'),
      });
      toast.success(
        `Invitation reissued to ${inv.target_email}. A new link has been created — the previous link is no longer valid.`,
      );
    } catch (e) {
      toast.error(
        `Step 1 succeeded (old invitation revoked) but step 2 failed (could not issue a fresh one): ${
          e?.detail || e?.message || 'issue failed'
        }. Use "Invite" to retry.`,
      );
    } finally {
      await refresh();
    }
  }, [accountId, refresh]);

  const onRevokeMember = useCallback(async (uid) => {
    try {
      await revokeMember({ accountId, targetUserId: uid });
      toast.success('member revoked');
      await refresh();
    } catch (e) { toast.error(String(e?.detail || e?.message || 'revoke failed')); }
  }, [accountId, refresh]);

  const onModifyRole = useCallback(async ({ targetUserId, newRole }) => {
    try {
      await modifyRole({ accountId, targetUserId, newRole });
      toast.success(`role updated to ${newRole}`);
      setRoleDialog(null);
      await refresh();
    } catch (e) { toast.error(String(e?.detail || e?.message || 'role modify failed')); }
  }, [accountId, refresh]);

  const onSuspend = useCallback(async ({ targetUserId, reason, expiresAt, isEmergency }) => {
    try {
      await suspendMember({ accountId, targetUserId, reason, expiresAt, isEmergency });
      toast.success('member suspended');
      setSuspendDialog(null);
      await refresh();
    } catch (e) { toast.error(String(e?.detail || e?.message || 'suspend failed')); }
  }, [accountId, refresh]);

  const onUnsuspend = useCallback(async (id) => {
    try {
      await unsuspend({ accountId, suspensionId: id });
      toast.success('suspension lifted');
      await refresh();
    } catch (e) { toast.error(String(e?.detail || e?.message || 'unsuspend failed')); }
  }, [accountId, refresh]);

  /* ─── bulk handlers ─── */
  const doBulkRevokeMembers = useCallback(async () => {
    const ids = Array.from(selectedMemberIds);
    if (!ids.length) return;
    if (ids.length > BULK_LIMITS.revoke) {
      toast.error(`bulk revoke cap is ${BULK_LIMITS.revoke}`); return;
    }
    try {
      const summary = await bulkRevokeMembers({ accountId, targetUserIds: ids });
      setBulkResult({ title: 'Bulk Member Revoke', summary, idField: 'target_user_id' });
      setSelectedMemberIds(new Set());
      await refresh();
    } catch (e) { toast.error(String(e?.detail || 'bulk revoke failed')); }
  }, [accountId, selectedMemberIds, refresh]);

  const doBulkRevokeInvitations = useCallback(async () => {
    const ids = Array.from(selectedInvitationIds);
    if (!ids.length) return;
    if (ids.length > BULK_LIMITS.inviteRevoke) {
      toast.error(`bulk invite revoke cap is ${BULK_LIMITS.inviteRevoke}`); return;
    }
    try {
      const summary = await bulkRevokeInvitations({ accountId, invitationIds: ids });
      setBulkResult({ title: 'Bulk Invitation Revoke', summary, idField: 'invitation_id' });
      setSelectedInvitationIds(new Set());
      await refresh();
    } catch (e) { toast.error(String(e?.detail || 'bulk invite revoke failed')); }
  }, [accountId, selectedInvitationIds, refresh]);

  const doBulkUnsuspend = useCallback(async () => {
    const ids = Array.from(selectedSuspensionIds);
    if (!ids.length) return;
    if (ids.length > BULK_LIMITS.unsuspend) {
      toast.error(`bulk unsuspend cap is ${BULK_LIMITS.unsuspend}`); return;
    }
    try {
      const summary = await bulkUnsuspend({ accountId, suspensionIds: ids });
      setBulkResult({ title: 'Bulk Unsuspend', summary, idField: 'suspension_id' });
      setSelectedSuspensionIds(new Set());
      await refresh();
    } catch (e) { toast.error(String(e?.detail || 'bulk unsuspend failed')); }
  }, [accountId, selectedSuspensionIds, refresh]);

  const doBulkRoleDemote = useCallback(async (newRole) => {
    const ids = Array.from(selectedMemberIds);
    if (!ids.length) return;
    if (ids.length > BULK_LIMITS.roleModify) {
      toast.error(`bulk role-modify cap is ${BULK_LIMITS.roleModify}`); return;
    }
    const items = ids.map((uid) => ({ target_user_id: uid, new_role: newRole }));
    try {
      const summary = await bulkRoleModify({ accountId, items });
      setBulkResult({ title: `Bulk Role → ${newRole}`, summary, idField: 'item' });
      setSelectedMemberIds(new Set());
      await refresh();
    } catch (e) { toast.error(String(e?.detail || 'bulk role-modify failed')); }
  }, [accountId, selectedMemberIds, refresh]);

  /* ─── render ─── */
  if (loading) {
    return (
      <div className="flex h-[50vh] items-center justify-center" data-testid="emp-members-loading">
        <span className="text-sm text-slate-300">Loading member workspace…</span>
      </div>
    );
  }

  // F-D4 (RC 1.62 employer-auth doctrine) — calm empty stub.
  if (noMembership) {
    return <EmployerNoMembershipStub />;
  }

  if (accessError) {
    return (
      <div className="mx-auto max-w-2xl p-6" data-testid="emp-members-access-error">
        <Card className="border-red-500/20 bg-red-50 p-6 dark:bg-red-950/20">
          <h2 className="mb-2 text-lg font-semibold text-rose-200 dark:text-red-300">
            Access denied
          </h2>
          <p className="text-sm text-rose-200/80 dark:text-red-300/80">{accessError}</p>
          <Button className="mt-4" onClick={() => navigate('/employer')}
            data-testid="emp-members-back-btn">
            Back to employer home
          </Button>
        </Card>
      </div>
    );
  }

  const isAdmin    = myRole === 'admin' || myRole === 'owner';
  const isReviewer = myRole === 'reviewer';

  /* Reviewer gets a strictly-scoped view per Amendment M / S6 RLS. */
  if (isReviewer) {
    return (
      <ReviewerView
        orgName={orgName}
        myUserId={myUserId}
        members={members}
        invitations={invitations}
        suspensions={suspensions}
        audit={audit}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl p-4 sm:p-6" data-testid="emp-members-workspace">
      <header className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-300"
            data-testid="emp-members-org-label">{orgName}</p>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl"
            data-testid="emp-members-title">Member management</h1>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" data-testid="emp-members-my-role">
            you · {myRole}
          </Badge>
          {isAdmin && (
            <Button onClick={() => setInviteOpen(true)}
              data-testid="emp-members-invite-btn">
              Invite member
            </Button>
          )}
        </div>
      </header>

      <Tabs defaultValue="members" className="w-full" data-testid="emp-members-tabs">
        <TabsList className="mb-4 grid w-full grid-cols-2 sm:grid-cols-4">
          <TabsTrigger value="members"     data-testid="emp-members-tab-members">Members ({members.length})</TabsTrigger>
          <TabsTrigger value="invitations" data-testid="emp-members-tab-invitations">Invitations ({invitations.length})</TabsTrigger>
          <TabsTrigger value="suspensions" data-testid="emp-members-tab-suspensions">Suspensions ({suspensions.filter(s => s.status==='active').length})</TabsTrigger>
          <TabsTrigger value="audit"       data-testid="emp-members-tab-audit">Audit ({audit.length})</TabsTrigger>
        </TabsList>

        {/* Roles & permissions explainer — surfaces the four-role
            authority model in operational language. The full matrix
            lives at /employer/help/permissions. */}
        <Card
          className="mb-4 border-ndor-edge p-4"
          data-testid="emp-members-permissions-explainer"
        >
          <div className="flex items-start gap-2.5">
            <ShieldCheck className="h-4 w-4 text-ndor-ink-muted shrink-0 mt-0.5" />
            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-medium text-ndor-ink">
                  Roles &amp; permissions
                </span>
                <Link
                  to="/employer/help/permissions"
                  className="text-ndor-ink-muted underline-offset-2 hover:underline"
                  data-testid="emp-members-permissions-link"
                >
                  Full matrix &rarr;
                </Link>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {['owner', 'admin', 'recruiter', 'reviewer'].map((r) => (
                  <li
                    key={r}
                    className="border border-ndor-edge rounded-md px-2.5 py-1.5"
                    data-testid={`emp-members-role-card-${r}`}
                  >
                    <Badge variant="outline" className="mr-1.5 text-[10px]">
                      {ROLE_LABEL[r]}
                    </Badge>
                    <span className="text-ndor-ink-secondary leading-relaxed">
                      {ROLE_DESCRIPTION[r]}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>

        <TabsContent value="members">
          <MembersList
            members={members}
            myUserId={myUserId}
            isAdmin={isAdmin}
            selected={selectedMemberIds}
            setSelected={setSelectedMemberIds}
            onModifyRole={(m) => setRoleDialog({ member: m })}
            onSuspend={(m) => setSuspendDialog({ member: m })}
            onRevoke={(uid) => {
              if (window.confirm('Revoke this member? This cannot be undone.')) {
                onRevokeMember(uid);
              }
            }}
            onBulkRevoke={doBulkRevokeMembers}
            onBulkRoleDemote={doBulkRoleDemote}
            bulkLimits={BULK_LIMITS}
          />
        </TabsContent>

        <TabsContent value="invitations">
          <InvitationsList
            invitations={invitations}
            isAdmin={isAdmin}
            selected={selectedInvitationIds}
            setSelected={setSelectedInvitationIds}
            onRevoke={(id) => {
              if (window.confirm('Revoke this pending invitation?')) onRevokeInvitation(id);
            }}
            onReissue={(inv) => {
              if (window.confirm(
                `Reissue invitation to ${inv.target_email}?\n\n` +
                `This revokes the current invitation and issues a fresh one ` +
                `with a NEW link. The previous link will no longer work.`,
              )) {
                onReissueInvitation(inv);
              }
            }}
            onBulkRevoke={doBulkRevokeInvitations}
            bulkLimit={BULK_LIMITS.inviteRevoke}
          />
        </TabsContent>

        <TabsContent value="suspensions">
          <SuspensionsList
            suspensions={suspensions}
            isAdmin={isAdmin}
            selected={selectedSuspensionIds}
            setSelected={setSelectedSuspensionIds}
            onUnsuspend={onUnsuspend}
            onBulkUnsuspend={doBulkUnsuspend}
            bulkLimit={BULK_LIMITS.unsuspend}
          />
        </TabsContent>

        <TabsContent value="audit">
          <AuditFeed audit={audit} />
        </TabsContent>
      </Tabs>

      {inviteOpen && (
        <InviteDialog
          onClose={() => setInviteOpen(false)}
          onSubmit={onInvite}
        />
      )}
      {roleDialog && (
        <RoleModifyDialog
          member={roleDialog.member}
          onClose={() => setRoleDialog(null)}
          onSubmit={onModifyRole}
        />
      )}
      {suspendDialog && (
        <SuspendDialog
          member={suspendDialog.member}
          onClose={() => setSuspendDialog(null)}
          onSubmit={onSuspend}
        />
      )}
      {bulkResult && (
        <BulkResultModal
          title={bulkResult.title}
          summary={bulkResult.summary}
          idField={bulkResult.idField}
          onClose={() => setBulkResult(null)}
        />
      )}
    </div>
  );
}
